import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

type IConsumerPropsSelectorFn<T> = (contextValue: T) => any;
type IConsumerPropsSelector<T> = string | IConsumerPropsSelectorFn<T>;

/**
 * @example
 *
 * function MyComponent({settings}) {
 *   return (
 *     <div>
 *       <pre>{JSON.stringify({settings})}</pre>
 *     </div>
 *   )
 * }
 *
 * const MyComponentWithSettings = withContextConsumer<ISettings>(SettingsConsumer, 'settings')(MyComponent);
 *
 * // OR
 *
 * const MyComponentWithSettings = withContextConsumer<ISettings>(SettingsConsumer, (settings) => ({settings}))(MyComponent);
 */
function withContextConsumer<T>(
  Consumer: React.Consumer<T>,
  propsSelector: IConsumerPropsSelector<T>,
) {
  return function (WrappedComponent) {
    const propsSelectorFn =
      typeof propsSelector === 'string'
        ? (contextValue) => ({ [propsSelector as string]: contextValue })
        : propsSelector;

    function ComponentWithContext(props) {
      return (
        <Consumer>
          {(contextValue) => (
            <WrappedComponent {...props} {...propsSelectorFn(contextValue)} />
          )}
        </Consumer>
      );
    }

    ComponentWithContext.displayName = `WithContextConsumer(${
      WrappedComponent.displayName || WrappedComponent.name
    })`;

    // NOTE: https://reactjs.org/docs/higher-order-components.html#static-methods-must-be-copied-over
    // NOTE: use https://reactjs.org/docs/forwarding-refs.html if need ref to wrapped component
    return hoistNonReactStatics(ComponentWithContext, WrappedComponent);
  };
}

// NOTE:
// https://reactjs.org/docs/context.html#consuming-context-with-a-hoc
// could be replaced with recompose fromRenderProps
// https://github.com/acdlite/recompose/blob/master/docs/API.md#fromrenderprops

export default withContextConsumer;
